import { useState } from "react";
import { ArrowRight, CalendarDays, MapPin, Download } from "lucide-react";
import { EVENT } from "@/data/eventData";
import { HERO } from "@/constants/testIds";
import BrochureModal from "@/components/site/BrochureModal";
import Countdown from "@/components/site/Countdown";

export default function Hero() {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative min-h-screen w-full overflow-hidden bg-[#030712]"
    >
      {/* Full-page brochure cover artwork */}
      <img
        src="/img/brochure_padlock_lg.jpg"
        alt="Philippines National Cyber Resilience Forum — cyber padlock artwork"
        data-testid="hero-brochure-visual"
        className="absolute inset-0 w-full h-full object-cover object-center"
        loading="eager"
        decoding="async"
      />

      {/* Dark vignette so text stays legible on any viewport */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/70 via-[#030712]/40 to-[#030712]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#030712]/85 via-[#030712]/40 to-transparent" />
      <div className="absolute inset-0 bg-tech-grid opacity-25" />

      {/* Corner accents */}
      <div className="absolute top-24 left-6 lg:left-10 w-24 h-24 border-l-2 border-t-2 border-cyan-400/70" />
      <div className="absolute bottom-10 right-6 lg:right-10 w-24 h-24 border-r-2 border-b-2 border-cyan-400/70" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-28 pb-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.22em] uppercase text-cyan-300 border border-cyan-500/50 rounded-full px-3 py-1 bg-[#030712]/80 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            Collaborate. Innovate. Defend.
          </div>

          <h1 className="font-display mt-8 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.02] drop-shadow-[0_2px_20px_rgba(0,0,0,0.65)]">
            Philippines National{" "}
            <span className="text-cyan-300">Cyber Resilience</span> Forum
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-200/95 max-w-2xl drop-shadow-[0_2px_16px_rgba(0,0,0,0.7)]">
            Defending the Digital Republic — Government–Industry Collaboration
            for Cyber Resilience.
          </p>

          <div className="mt-10 flex flex-wrap gap-6 text-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-cyan-500/15 border border-cyan-500/40 backdrop-blur">
                <CalendarDays className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-cyan-200/80">
                  Dates
                </div>
                <div className="font-semibold">{EVENT.dates}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-cyan-500/15 border border-cyan-500/40 backdrop-blur">
                <MapPin className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-cyan-200/80">
                  Venue
                </div>
                <div className="font-semibold">{EVENT.venue}</div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <Countdown />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid={HERO.cta}
              className="inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-6 py-3 transition-colors"
            >
              Submit an Enquiry <ArrowRight className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="hero-cta-brochure"
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 hover:bg-slate-200 px-6 py-3 font-semibold transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Brochure
            </button>
            <a
              href="#agenda"
              data-testid={HERO.ctaAgenda}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/60 text-cyan-100 hover:bg-cyan-400/10 px-6 py-3 font-semibold transition-colors backdrop-blur"
            >
              View Agenda
            </a>
          </div>
        </div>

        {/* Bottom stats bar */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
          {[
            ["25+", "Speakers"],
            ["2", "Days"],
            ["15+", "Sectors"],
            ["1", "Mission"],
          ].map(([k, v]) => (
            <div key={v} className="border-l border-cyan-500/40 pl-4">
              <div className="font-display text-3xl font-bold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                {k}
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-cyan-200/80 mt-1">
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>

      <BrochureModal open={open} onOpenChange={setOpen} />
    </section>
  );
}
